import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import '../styles/PostCard.css';

const TYPE_META = {
    Help:    { cls: 'badge--help'    },
    Rent:    { cls: 'badge--rent'    },
    Sell:    { cls: 'badge--sell'    },
    Alert:   { cls: 'badge--alert'   },
    General: { cls: 'badge--general' },
};

const PostCard = ({ post, user, onInteraction, onComment }) => {
    const [commentContent,     setCommentContent]     = useState('');
    const [activeInteraction,  setActiveInteraction]  = useState(null);
    const [interactionDetails, setInteractionDetails] = useState('');
    const [interactionPrice,   setInteractionPrice]   = useState('');
    const [interactionError,   setInteractionError]   = useState('');
    const [submitting,         setSubmitting]         = useState(false);

    const currentUserId = user?.user?._id;
    const authorId      = post.author?._id || post.user;
    const isAuthor      = authorId === currentUserId;
    const hasInteracted = post.interactions.some(
        i => (i.user?._id || i.user) === currentUserId
    );

    const ai          = post.ai_categorization;
    const isAlert     = ai?.is_alert === true;
    const postType    = ai?.post_type_suggestion;
    const category    = ai?.category;
    const typeMeta    = TYPE_META[postType] ?? null;

    const authorName    = post.author?.name || 'Anonymous';
    const authorInitial = authorName[0].toUpperCase();

    const activeOffers = post.interactions.filter(
        i => i.type !== 'help' && !i.is_completed
    );

    // ── Interaction form ──────────────────────────────────────────────────────
    const openInteraction = (type) => {
        if (activeInteraction === type) {
            setActiveInteraction(null);
        } else {
            setActiveInteraction(type);
        }
        setInteractionDetails('');
        setInteractionPrice('');
        setInteractionError('');
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!interactionDetails.trim()) {
            setInteractionError('Please add some details about your offer.');
            return;
        }
        const needsPrice = activeInteraction === 'rent' || activeInteraction === 'sell';
        const priceNum   = interactionPrice ? parseFloat(interactionPrice) : null;
        if (needsPrice && (priceNum == null || priceNum <= 0)) {
            setInteractionError('A price greater than 0 is required.');
            return;
        }

        setSubmitting(true);
        setInteractionError('');

        try {
            await onInteraction(post._id, activeInteraction, interactionDetails.trim(), priceNum);
            // Reset only after the call resolves — form stays visible until then
            setActiveInteraction(null);
            setInteractionDetails('');
            setInteractionPrice('');
            setInteractionError('');
        } catch {
            // Feed.js surfaces the error banner; keep form open so user can retry
        } finally {
            setSubmitting(false);
        }
    };

    // ── Comment ───────────────────────────────────────────────────────────────
    const handleCommentSubmit = (e) => {
        e.preventDefault();
        if (commentContent.trim()) {
            onComment(post._id, commentContent.trim());
            setCommentContent('');
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="post-card">

            {/* ── Header ── */}
            <div className="post-card-header">
                <div className="post-avatar" aria-hidden="true">{authorInitial}</div>

                <div className="post-meta">
                    <span className="post-author-name">{authorName}</span>
                    <span className="post-meta-sub">
                        {post.distance !== undefined
                            ? `${Math.round(post.distance)}m away`
                            : 'Nearby'}
                        {' · '}
                        {post.createdAt
                            ? formatDistanceToNow(new Date(post.createdAt)) + ' ago'
                            : 'Just now'}
                    </span>
                </div>

                {(isAlert || typeMeta || (category && category !== 'General')) && (
                    <div className="post-badges">
                        {isAlert && (
                            <span className="badge badge--alert">⚠️ Alert</span>
                        )}
                        {typeMeta && !isAlert && (
                            <span className={`badge ${typeMeta.cls}`}>{postType}</span>
                        )}
                        {category && category !== 'General' && (
                            <span className="badge badge--category">{category}</span>
                        )}
                    </div>
                )}
            </div>

            {/* ── Body ── */}
            <div className="post-body">
                <p>{post.content}</p>
            </div>

            {/* ── Competitive offers (non-author) ── */}
            {!isAuthor && activeOffers.length > 0 && (
                <div className="offers-section">
                    <p className="offers-title">Current Offers ({activeOffers.length})</p>
                    <div className="offers-list">
                        {activeOffers.map((offer, i) => (
                            <div key={i} className="offer-item">
                                <span className={`badge badge--${offer.type}`}>
                                    {offer.type.charAt(0).toUpperCase() + offer.type.slice(1)}
                                </span>
                                {offer.price != null && (
                                    <span className="offer-price">${offer.price.toFixed(2)}</span>
                                )}
                                <p className="offer-details">{offer.details || '—'}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Offers received (author) ── */}
            {isAuthor && post.interactions.length > 0 && (
                <div className="offers-section">
                    <p className="offers-title">
                        Offers Received ({post.interactions.length})
                    </p>
                    <div className="offers-list">
                        {post.interactions.map((offer, i) => (
                            <div key={i} className="offer-item">
                                <div className="offer-item-top">
                                    <span className={`badge badge--${offer.type}`}>
                                        {offer.type.charAt(0).toUpperCase() + offer.type.slice(1)}
                                    </span>
                                    <span className="offer-user">
                                        {offer.user?.name || 'Unknown'}
                                        {offer.responder_rating != null && (
                                            <span className="offer-rating">
                                                {' '}· ★ {offer.responder_rating}/5
                                            </span>
                                        )}
                                    </span>
                                    <span className="offer-time">
                                        {offer.createdAt
                                            ? formatDistanceToNow(new Date(offer.createdAt)) + ' ago'
                                            : '—'}
                                    </span>
                                </div>
                                {offer.price != null && (
                                    <span className="offer-price">${offer.price.toFixed(2)}</span>
                                )}
                                <p className="offer-details">{offer.details || '—'}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Action buttons (non-author) ── */}
            {!isAuthor && (
                <div className="post-actions">
                    {['help', 'rent', 'sell'].map(type => (
                        <button
                            key={type}
                            className={`action-btn action-btn--${type}${activeInteraction === type ? ' action-btn--active' : ''}`}
                            onClick={() => openInteraction(type)}
                        >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                    ))}
                </div>
            )}

            {/* ── Inline interaction form ── */}
            {activeInteraction && (
                <form className="interaction-form" onSubmit={handleFormSubmit} noValidate>
                    <label className="interaction-form-label">
                        {activeInteraction === 'help'
                            ? 'Describe how you can help'
                            : `${activeInteraction.charAt(0).toUpperCase() + activeInteraction.slice(1)} offer details`}
                    </label>
                    <textarea
                        className="interaction-form-textarea"
                        value={interactionDetails}
                        onChange={e => setInteractionDetails(e.target.value)}
                        placeholder={
                            activeInteraction === 'help'
                                ? 'e.g. I can be there in 10 minutes…'
                                : 'e.g. Good condition, available from Monday…'
                        }
                        rows={3}
                        disabled={submitting}
                    />
                    {(activeInteraction === 'rent' || activeInteraction === 'sell') && (
                        <div className="interaction-form-price-row">
                            <label
                                className="interaction-form-label"
                                htmlFor={`price-${post._id}`}
                            >
                                Price ($)
                            </label>
                            <input
                                id={`price-${post._id}`}
                                type="number"
                                className="interaction-form-price"
                                value={interactionPrice}
                                onChange={e => setInteractionPrice(e.target.value)}
                                placeholder="0.00"
                                min="0.01"
                                step="0.01"
                                disabled={submitting}
                            />
                        </div>
                    )}
                    {interactionError && (
                        <span className="interaction-form-error" role="alert">
                            {interactionError}
                        </span>
                    )}
                    <div className="interaction-form-actions">
                        <button type="submit" className="form-submit-btn" disabled={submitting}>
                            {submitting ? 'Submitting…' : 'Submit'}
                        </button>
                        <button
                            type="button"
                            className="form-cancel-btn"
                            disabled={submitting}
                            onClick={() => {
                                setActiveInteraction(null);
                                setInteractionDetails('');
                                setInteractionPrice('');
                                setInteractionError('');
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {/* ── Comments ── */}
            {(post.comments.length > 0 || isAuthor || hasInteracted) && (
                <div className="comments-section">
                    {post.comments.length > 0 && (
                        <div className="comments-list">
                            {post.comments.map((c, i) => (
                                <div
                                    key={i}
                                    className={[
                                        'comment-item',
                                        (c.analysis?.is_toxic || c.analysis?.intent === 'warning')
                                            ? 'comment-item--warning'
                                            : c.analysis?.intent === 'updated_offer'
                                            ? 'comment-item--offer'
                                            : '',
                                    ].filter(Boolean).join(' ')}
                                >
                                    <div className="comment-header">
                                        <span className="comment-author">
                                            {c.user?.name || 'Anonymous'}
                                        </span>
                                        {c.analysis?.intent === 'updated_offer' && (
                                            <span className="comment-badge comment-badge--offer">
                                                💰 ${c.analysis.detected_price?.toFixed(2)}
                                            </span>
                                        )}
                                        {(c.analysis?.is_toxic || c.analysis?.intent === 'warning') && (
                                            <span className="comment-badge comment-badge--warning">
                                                ⚠️ Warning
                                            </span>
                                        )}
                                        <span className="comment-time">
                                            {c.createdAt
                                                ? formatDistanceToNow(new Date(c.createdAt)) + ' ago'
                                                : 'Just now'}
                                        </span>
                                    </div>
                                    <p className="comment-body">{c.content}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {(isAuthor || hasInteracted) && (
                        <form className="comment-form" onSubmit={handleCommentSubmit}>
                            <input
                                type="text"
                                className="comment-input"
                                value={commentContent}
                                onChange={e => setCommentContent(e.target.value)}
                                placeholder="Write a comment or negotiate terms…"
                            />
                            <button type="submit" className="comment-submit-btn">Post</button>
                        </form>
                    )}
                </div>
            )}

        </div>
    );
};

export default PostCard;
